import { Box, Snackbar, TextField } from '@mui/material';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TemplateFieldsSection from '../components/common/TemplateFieldsSection/TemplateFieldsSection';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import { DisableContext } from '../contexts/DisableContextProvider';
import { useCreateAccountTemplate, useGetAccountTemplateById, useUpdateAccountTemplate } from '../services/react-query/account_templates';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { TemplateField } from '../types/FieldCommon';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator, uuidFinder } from '../utility/inputUtils';
import TagsSection from '../components/common/TagsSection/TagsSection';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import { ROUTE_PATHS } from '../routes/Routes';
import { useArchiveAccountTemplate } from '../services/react-query/account_templates/useArchiveAccountTemplate';
import { useDuplicateAccountTemplate } from '../services/react-query/account_templates/useDuplicateAccountTemplate';
import AccordionSection from '../components/AccordionSection';
import { useIsMutating } from '@tanstack/react-query';
import { mutationKeys } from '../services/react-query/mutationKeys';
import DuplicateModal from '../components/DuplicateModal';
import _ from 'lodash';
import { AccountTemplate, AccountTemplateSaveResponse } from '../types/AccountTemplate';
import { useGetAllPreferences } from '../services/react-query/preferences';
import PreferencePicker from '../components/preferences/picker/PreferencePicker';
import { Preference, PREFERENCES_VALID_KEYS } from '../types/Preference';
import { useGetAllEmployees } from '../services/react-query/employees';
import EmployeeSearchAutocomplete from '../components/common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';

export default function AccountTemplateItem() {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<AccountTemplate | null>(null);
  const [saved, setSaved] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const isNew = uuid === 'new';

  const { data, isLoading } = useGetAccountTemplateById({ uuid });

  const { data: employeesData } = useGetAllEmployees({ params: { per_page: 0 } });
  const employees = employeesData?.data || [];

  const { mutate: createTemplate, isPending: isCreating } = useCreateAccountTemplate({
    onSuccessCallback: () => null,
  });

  const { mutate: updateTemplate, isPending: isUpdating } = useUpdateAccountTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => setSaved(true),
  });
  const { mutate: archiveTemplate } = useArchiveAccountTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.ACCOUNT_TEMPLATES}`),
  });

  const { mutate: duplicateTemplateMutation } = useDuplicateAccountTemplate((newUuid) => {
    navigate(`/${ROUTE_PATHS.ACCOUNT_TEMPLATES}/${newUuid}`);
    setIsDuplicateModalOpen(false);
  });

  useEffect(() => {
    if (data) setTemplate(data);
  }, [data]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable('archived', !!template?.discarded_at);

  const { data: preferencesData, isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.ACCOUNT_SCHOOL_DISTRICT, PREFERENCES_VALID_KEYS.ACCOUNT_STATUS, PREFERENCES_VALID_KEYS.ACCOUNT_TYPE],
      },
    },
  });

  const preferences = preferencesData ?? [];

  const districtOptions = useMemo(
    () =>
      _.filter(preferences, {
        key: PREFERENCES_VALID_KEYS.ACCOUNT_SCHOOL_DISTRICT,
      }),
    [preferences]
  );

  const statusOptions = useMemo(
    () =>
      _.filter(preferences, {
        key: PREFERENCES_VALID_KEYS.ACCOUNT_STATUS,
      }),
    [preferences]
  );

  const typeOptions = useMemo(
    () =>
      _.filter(preferences, {
        key: PREFERENCES_VALID_KEYS.ACCOUNT_TYPE,
      }),
    [preferences]
  );

  const showLoading = !template || isLoading;
  if (showLoading) return <Loading />;

  const handleSave = () => {
    if (!template) return;

    const payload: AccountTemplateSaveResponse = {
      account_template: template,
    };

    if (isNew) createTemplate(payload);
    else updateTemplate(payload);
  };

  const setFields = (fields: TemplateField[]) => setTemplate({ ...template, fields });

  const handleDuplicate = (name: string) => {
    duplicateTemplateMutation({ uuid: _.toString(uuid), name });
  };

  return (
    <Box>
      <ItemHeader
        title={`${isNew ? 'Create' : 'Update'} Account Template`}
        onSave={handleSave}
        archived={!!template.discarded_at}
        disableActions={isCreating || isUpdating}
        showMoreOptions={
          !isNew ? (
            <MoreOptions onArchive={archiveTemplate} onDuplicate={() => setIsDuplicateModalOpen(true)} name={template?.name} itemName="account template" />
          ) : null
        }
      />

      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.ACCOUNT_TEMPLATES_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          {/* LEFT COLUMN */}
          <Box sx={inputDividerStyling}>
            <TextField
              label="Name"
              value={template?.name ?? ''}
              autoFocus
              onChange={(e) =>
                setTemplate({
                  ...template,
                  name: e.target.value,
                })
              }
              disabled={disabled}
              {...errorPopulator(template, 'name')}
            />

            <EmployeeSearchAutocomplete
              isDisabled={disabled}
              label="Account Manager"
              error={errorPopulator(template, 'manager')}
              value={uuidFinder(employees, template?.manager_uuid)}
              onChange={(employee) => setTemplate((prev) => (prev ? { ...prev, manager_uuid: employee?.uuid } : prev))}
            />
          </Box>

          {/* RIGHT COLUMN */}
          <Box sx={inputDividerStyling}>
            <PreferencePicker
              label="School District"
              options={districtOptions}
              loading={isGetAllPreferencesFetching}
              value={uuidFinder(districtOptions, template?.school_district_uuid) ?? null}
              onValueChange={(v) => {
                const pref = v as Preference | null;
                setTemplate((prev) => (prev ? { ...prev, school_district_uuid: pref?.uuid ?? undefined } : prev));
              }}
              disabled={disabled}
              helperText={errorPopulator(template, 'school_district')?.helperText}
            />

            <PreferencePicker
              label="Status"
              options={statusOptions}
              loading={isGetAllPreferencesFetching}
              value={uuidFinder(statusOptions, template?.status_uuid) ?? null}
              onValueChange={(v) => {
                const pref = v as Preference | null;
                setTemplate((prev) => (prev ? { ...prev, status_uuid: pref?.uuid ?? undefined } : prev));
              }}
              disabled={disabled}
              helperText={errorPopulator(template, 'status')?.helperText}
            />

            <PreferencePicker
              label="Type"
              options={typeOptions}
              loading={isGetAllPreferencesFetching}
              value={uuidFinder(typeOptions, template?.type_uuid) ?? null}
              onValueChange={(v) => {
                const pref = v as Preference | null;
                setTemplate((prev) => (prev ? { ...prev, type_uuid: pref?.uuid ?? undefined } : prev));
              }}
              disabled={disabled}
              helperText={errorPopulator(template, 'type')?.helperText}
            />
          </Box>
        </Box>
      </AccordionSection>

      <TemplateFieldsSection fields={template.fields} setFields={setFields} collapseId={UserSettingsCollapseKeys.ACCOUNT_TEMPLATES_ITEM_FIELDS} />

      <TagsSection
        tags={template.tags}
        collapseId={UserSettingsCollapseKeys.ACCOUNT_TEMPLATES_ITEM_TAGS}
        setTags={(tags) => setTemplate({ ...template, tags })}
      />

      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(name) => handleDuplicate(name)}
        defaultName={`${template?.name ?? ''} Copy`}
        itemName="account template"
      />

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
}
